/**
 * Expense claims. Employees draft a claim (items + receipts) and submit it;
 * payers work the "to pay" queue and mark claims paid or send them back.
 * The same list is embedded on an employee's profile.
 */
import { useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CURRENCIES } from "@offertli/shared";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Paginator } from "@primereact/ui/paginator";
import { Tag } from "@primereact/ui/tag";
import { Textarea } from "@primereact/ui/textarea";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import {
  DateInput,
  FilterTabs,
  Modal,
  SelectInput,
  toDateValue,
} from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import {
  apiDelete,
  apiGet,
  apiGetPage,
  apiPatch,
  apiPost,
  apiUpload,
} from "../../lib/api.js";
import { useMe, usePermissions } from "../../lib/auth.js";
import { prepareUpload } from "../../lib/images.js";
import {
  ConfirmButton,
  ErrorPanel,
  LoadingPanel,
  MutationError,
  formatDate,
  formatMoney,
} from "../shared.js";

type ExpenseType = "mileage" | "meal" | "lodging" | "supplies" | "other";
type ExpenseStatus = "draft" | "submitted" | "paid";
const PAGE_SIZE = 25;
const EXPENSE_TYPES: ExpenseType[] = [
  "mileage",
  "meal",
  "lodging",
  "supplies",
  "other",
];

interface ExpenseClaim {
  id: string;
  employee: { id: string; firstName: string; lastName: string };
  status: ExpenseStatus;
  currency: string;
  total: string;
  notes: string | null;
  submittedAt: string | null;
  paidAt: string | null;
  returnReason: string | null;
  returnedAt: string | null;
  version: number;
  items: Array<{
    id: string;
    date: string;
    type: ExpenseType;
    description: string;
    amount: string;
    currency: string;
    kilometers: string | null;
    projectId: string | null;
  }>;
  attachments: Array<{
    id: string;
    fileName: string;
    mimeType: string;
    downloadUrl: string;
    thumbnailUrl?: string;
  }>;
  createdAt: string;
}

interface DraftItem {
  date: string;
  type: ExpenseType;
  description: string;
  amount: string;
  kilometers: string;
  projectId: string;
}

const emptyItem = (): DraftItem => ({
  date: toDateValue(new Date()),
  type: "other",
  description: "",
  amount: "",
  kilometers: "",
  projectId: "",
});

function statusSeverity(status: ExpenseStatus) {
  if (status === "paid") return "success" as const;
  if (status === "submitted") return "info" as const;
  return "secondary" as const;
}

type Scope = "mine" | "toPay" | "all";

export function ExpensesPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { data: me } = useMe();
  const payer = permissions.hasAny(["expenses.pay", "expenses.view_all"]);
  const [scope, setScope] = useState<Scope>(() => {
    const status = new URLSearchParams(window.location.search).get("status");
    return payer && status === "submitted" ? "toPay" : "mine";
  });
  const [editing, setEditing] = useState<ExpenseClaim | "new" | null>(null);
  const query =
    scope === "mine"
      ? { employeeId: me?.user.id }
      : scope === "toPay"
        ? { status: "submitted" }
        : {};
  return (
    <>
      <PageHeader
        title={t("expenses.title")}
        subtitle={t("expenses.subtitle")}
        actions={
          permissions.has("expenses.submit") ? (
            <Button
              type="button"
              icon="pi pi-plus"
              onClick={() => setEditing("new")}
            >
              {t("expenses.newClaim")}
            </Button>
          ) : null
        }
      />
      {payer ? (
        <div className="mb-4">
          <FilterTabs
            value={scope}
            onChange={setScope}
            options={[
              { value: "mine", label: t("expenses.scope.mine") },
              { value: "toPay", label: t("expenses.scope.toPay") },
              { value: "all", label: t("expenses.scope.all") },
            ]}
          />
        </div>
      ) : null}
      {me ? (
        <ClaimList
          query={query}
          showEmployee={scope !== "mine"}
          onEdit={setEditing}
        />
      ) : (
        <LoadingPanel />
      )}
      {editing ? (
        <ClaimEditor
          claim={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

/** An employee's claims on their profile page. */
export function EmployeeExpensesPanel({ userId }: { userId: string }) {
  const { data: me } = useMe();
  const [editing, setEditing] = useState<ExpenseClaim | null>(null);
  return (
    <>
      <ClaimList
        query={{ employeeId: userId }}
        showEmployee={me?.user.id !== userId}
        onEdit={setEditing}
      />
      {editing ? (
        <ClaimEditor claim={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  );
}

function ClaimList({
  query,
  showEmployee,
  onEdit,
}: {
  query: { employeeId?: string; status?: string };
  showEmployee: boolean;
  onEdit: (claim: ExpenseClaim) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const scope = JSON.stringify(query);
  const [pagedScope, setPagedScope] = useState(scope);
  if (pagedScope !== scope) {
    // A different list starts on its first page.
    setPagedScope(scope);
    setPage(1);
  }
  const result = useQuery({
    queryKey: ["expense-claims", query, page],
    queryFn: () =>
      apiGet<{ data: ExpenseClaim[]; total: number }>("/expenses/claims", {
        query: { ...query, page, pageSize: PAGE_SIZE },
      }),
    placeholderData: (previous) => previous,
  });
  const claims = { ...result, data: result.data?.data };
  if (claims.isLoading) return <LoadingPanel />;
  if (claims.error) return <ErrorPanel error={claims.error} />;
  if (!claims.data?.length)
    return (
      <p className="rounded-xl border border-dashed border-app-border p-8 text-center text-sm text-app-muted">
        {t("expenses.empty")}
      </p>
    );
  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {claims.data.map((claim) => (
          <li
            key={claim.id}
            className="rounded-xl border border-app-border bg-app-surface"
          >
            <button
              type="button"
              aria-expanded={open === claim.id}
              onClick={() => setOpen(open === claim.id ? null : claim.id)}
              className="flex w-full flex-wrap items-center gap-3 p-4 text-left"
            >
              <i
                className={cx(
                  "pi text-xs text-app-muted",
                  open === claim.id ? "pi-chevron-down" : "pi-chevron-right",
                )}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">
                  {showEmployee
                    ? `${claim.employee.firstName} ${claim.employee.lastName}`
                    : claim.items[0]?.description || t("expenses.claim")}
                </span>
                <span className="block text-xs text-app-muted">
                  {t("expenses.itemCount", { count: claim.items.length })} ·{" "}
                  {formatDate(claim.submittedAt ?? claim.createdAt)}
                </span>
              </span>
              <span className="font-semibold tabular-nums">
                {formatMoney(claim.total, claim.currency)}
              </span>
              {claim.returnReason && claim.status === "draft" ? (
                <Tag severity="warn">{t("expenses.returned")}</Tag>
              ) : (
                <Tag severity={statusSeverity(claim.status)}>
                  {t(`expenses.statusLabels.${claim.status}`)}
                </Tag>
              )}
            </button>
            {open === claim.id ? (
              <ClaimDetail claim={claim} onEdit={() => onEdit(claim)} />
            ) : null}
          </li>
        ))}
      </ul>
      {(result.data?.total ?? 0) > PAGE_SIZE ? (
        <Paginator.Root
          page={page}
          total={result.data?.total ?? 0}
          itemsPerPage={PAGE_SIZE}
          onPageChange={(event: { value: number }) => setPage(event.value)}
        />
      ) : null}
    </div>
  );
}

function ClaimDetail({
  claim,
  onEdit,
}: {
  claim: ExpenseClaim;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: me } = useMe();
  const permissions = usePermissions();
  const fileInput = useRef<HTMLInputElement>(null);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState("");
  const own = me?.user.id === claim.employee.id;
  const canPay = permissions.has("expenses.pay") && !own;
  const reviewer =
    !own && permissions.hasAny(["expenses.pay", "expenses.view_all"]);
  const receiptsEditable =
    claim.status !== "paid" && (reviewer || (own && claim.status === "draft"));
  const refresh = () =>
    client.invalidateQueries({ queryKey: ["expense-claims"] });
  const act = useMutation({
    mutationFn: (
      action:
        | { kind: "submit" | "withdraw" | "pay" }
        | { kind: "return"; reason: string }
        | { kind: "delete" },
    ) =>
      action.kind === "delete"
        ? apiDelete(`/expenses/claims/${claim.id}?version=${claim.version}`)
        : apiPost(`/expenses/claims/${claim.id}/${action.kind}`, {
            version: claim.version,
            ...(action.kind === "return" ? { reason: action.reason } : {}),
          }),
    onSuccess: async () => {
      setReturning(false);
      setReason("");
      await refresh();
    },
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", await prepareUpload(file));
      return apiUpload(`/expenses/claims/${claim.id}/receipts`, form);
    },
    onSuccess: refresh,
  });
  const removeReceipt = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/expenses/claims/${claim.id}/receipts/${id}`),
    onSuccess: refresh,
  });

  return (
    <div className="space-y-4 border-t border-app-border p-4">
      {claim.returnReason && claim.status === "draft" ? (
        <p className="rounded-lg border border-warning-border bg-warning-soft px-3 py-2 text-sm text-warning">
          <strong>{t("expenses.returnedBecause")}</strong> {claim.returnReason}
        </p>
      ) : null}
      <MutationError error={act.error || upload.error || removeReceipt.error} />
      <table className="w-full text-sm">
        <tbody className="divide-y divide-app-border">
          {claim.items.map((item) => (
            <tr key={item.id}>
              <td className="w-24 py-1.5 text-app-muted">
                {formatDate(item.date)}
              </td>
              <td className="py-1.5">
                {item.description}
                <span className="ml-2 text-xs text-app-muted">
                  {t(`expenses.typeLabels.${item.type}`)}
                  {item.kilometers ? ` · ${item.kilometers} km` : ""}
                </span>
              </td>
              <td className="py-1.5 text-right tabular-nums">
                {formatMoney(item.amount, item.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {claim.notes ? (
        <p className="text-sm text-app-muted">{claim.notes}</p>
      ) : null}
      <div>
        <p className="mb-2 text-sm font-medium">{t("expenses.receipts")}</p>
        <div className="flex flex-wrap gap-2">
          {claim.attachments.map((receipt) => (
            <span
              key={receipt.id}
              className="flex items-center gap-2 rounded-lg border border-app-border px-2 py-1 text-sm"
            >
              {receipt.thumbnailUrl ? (
                <img
                  src={receipt.thumbnailUrl}
                  alt=""
                  className="h-8 w-8 rounded object-cover"
                />
              ) : (
                <i className="pi pi-file-pdf text-app-muted" />
              )}
              <a
                className="max-w-40 truncate text-brand hover:underline"
                href={receipt.downloadUrl}
                target="_blank"
                rel="noreferrer"
              >
                {receipt.fileName}
              </a>
              {receiptsEditable ? (
                <button
                  type="button"
                  className="text-app-muted hover:text-danger"
                  aria-label={t("common.delete")}
                  onClick={() => removeReceipt.mutate(receipt.id)}
                >
                  <i className="pi pi-times text-xs" />
                </button>
              ) : null}
            </span>
          ))}
          {!claim.attachments.length ? (
            <span className="text-sm text-app-muted">
              {t("expenses.noReceipts")}
            </span>
          ) : null}
        </div>
        {receiptsEditable ? (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate(file);
                event.target.value = "";
              }}
            />
            <Button
              type="button"
              size="small"
              variant="outlined"
              severity="secondary"
              icon="pi pi-camera"
              className="mt-2"
              loading={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {t("expenses.uploadReceipt")}
            </Button>
          </>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2 border-t border-app-border pt-3">
        {own && claim.status === "draft" ? (
          <>
            <Button
              type="button"
              size="small"
              icon="pi pi-send"
              loading={act.isPending}
              onClick={() => act.mutate({ kind: "submit" })}
            >
              {t("expenses.submit")}
            </Button>
            <Button
              type="button"
              size="small"
              variant="outlined"
              severity="secondary"
              icon="pi pi-pencil"
              onClick={onEdit}
            >
              {t("common.edit")}
            </Button>
            <ConfirmButton
              onConfirm={() => act.mutate({ kind: "delete" })}
              confirmMessage={t("expenses.deleteConfirm")}
            >
              {t("common.delete")}
            </ConfirmButton>
          </>
        ) : null}
        {own && claim.status === "submitted" ? (
          <Button
            type="button"
            size="small"
            variant="outlined"
            severity="secondary"
            icon="pi pi-undo"
            loading={act.isPending}
            onClick={() => act.mutate({ kind: "withdraw" })}
          >
            {t("expenses.withdraw")}
          </Button>
        ) : null}
        {canPay && claim.status === "submitted" ? (
          <>
            <Button
              type="button"
              size="small"
              icon="pi pi-check"
              loading={act.isPending}
              onClick={() => act.mutate({ kind: "pay" })}
            >
              {t("expenses.pay")}
            </Button>
            <Button
              type="button"
              size="small"
              variant="outlined"
              severity="warn"
              icon="pi pi-reply"
              onClick={() => setReturning(true)}
            >
              {t("expenses.return")}
            </Button>
          </>
        ) : null}
        {claim.status === "paid" && claim.paidAt ? (
          <span className="text-sm text-app-muted">
            {t("expenses.paidOn", { date: formatDate(claim.paidAt) })}
          </span>
        ) : null}
      </div>
      <Modal
        open={returning}
        onClose={() => setReturning(false)}
        title={t("expenses.returnTitle")}
        footer={
          <>
            <Button
              type="button"
              variant="outlined"
              severity="secondary"
              onClick={() => setReturning(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              severity="warn"
              disabled={!reason.trim()}
              loading={act.isPending}
              onClick={() =>
                act.mutate({ kind: "return", reason: reason.trim() })
              }
            >
              {t("expenses.return")}
            </Button>
          </>
        }
      >
        <Field label={t("expenses.returnReason")} required>
          <Textarea
            rows={3}
            className="w-full"
            value={reason}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setReason(event.target.value)
            }
          />
        </Field>
      </Modal>
    </div>
  );
}

function ClaimEditor({
  claim,
  onClose,
}: {
  claim: ExpenseClaim | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [currency, setCurrency] = useState(claim?.currency ?? "CHF");
  const [notes, setNotes] = useState(claim?.notes ?? "");
  const [items, setItems] = useState<DraftItem[]>(
    claim?.items.map((item) => ({
      date: item.date,
      type: item.type,
      description: item.description,
      amount: item.amount,
      kilometers: item.kilometers ?? "",
      projectId: item.projectId ?? "",
    })) ?? [emptyItem()],
  );
  const projects = useQuery({
    queryKey: ["expense-projects"],
    queryFn: () =>
      apiGetPage<{
        data: { id: string; number: string; title: string; status: string };
      }>("/projects", { pageSize: 100 }),
  });
  const projectOptions = [
    { value: "", label: t("expenses.noProject") },
    ...(projects.data?.data ?? [])
      .filter((node) => node.data.status !== "cancelled")
      .map((node) => ({
        value: node.data.id,
        label: `${node.data.number} · ${node.data.title}`,
      })),
  ];
  const update = (index: number, patch: Partial<DraftItem>) =>
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  const valid = items.every(
    (item) => item.date && item.description.trim() && Number(item.amount) > 0,
  );
  const total = items.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        currency,
        notes: notes.trim() || null,
        items: items.map((item) => ({
          date: item.date,
          type: item.type,
          description: item.description.trim(),
          amount: Number(item.amount),
          currency,
          kilometers:
            item.type === "mileage" && item.kilometers
              ? Number(item.kilometers)
              : null,
          projectId: item.projectId || null,
        })),
      };
      return claim
        ? apiPatch(`/expenses/claims/${claim.id}`, {
            ...payload,
            version: claim.version,
          })
        : apiPost("/expenses/claims", payload);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["expense-claims"] });
      onClose();
    },
  });
  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={claim ? t("expenses.editClaim") : t("expenses.newClaim")}
      footer={
        <>
          <span className="mr-auto self-center text-sm font-semibold tabular-nums">
            {formatMoney(total, currency)}
          </span>
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!valid}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t("expenses.saveDraft")}
          </Button>
        </>
      }
    >
      <MutationError error={save.error} />
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-2 gap-2 rounded-lg border border-app-border p-3 sm:grid-cols-6"
          >
            <div className="col-span-1 sm:col-span-2">
              <DateInput
                ariaLabel={t("expenses.date")}
                value={item.date}
                onChange={(value) => update(index, { date: value })}
              />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <SelectInput
                ariaLabel={t("expenses.type")}
                value={item.type}
                onChange={(value) => update(index, { type: value })}
                options={EXPENSE_TYPES.map((type) => ({
                  value: type,
                  label: t(`expenses.typeLabels.${type}`),
                }))}
              />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <Input
                fluid
                inputMode="decimal"
                aria-label={t("expenses.amount")}
                placeholder={t("expenses.amount")}
                value={item.amount}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  update(index, {
                    amount: event.target.value.replace(",", "."),
                  })
                }
              />
            </div>
            <div className="col-span-2 sm:col-span-4">
              <Input
                fluid
                aria-label={t("expenses.description")}
                placeholder={t("expenses.description")}
                value={item.description}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  update(index, { description: event.target.value })
                }
              />
            </div>
            {item.type === "mileage" ? (
              <div className="col-span-2 sm:col-span-2">
                <Input
                  fluid
                  inputMode="decimal"
                  aria-label={t("expenses.kilometers")}
                  placeholder={t("expenses.kilometers")}
                  value={item.kilometers}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    update(index, {
                      kilometers: event.target.value.replace(",", "."),
                    })
                  }
                />
              </div>
            ) : null}
            <div className="col-span-2 sm:col-span-5">
              <SelectInput
                ariaLabel={t("expenses.project")}
                value={item.projectId}
                onChange={(value) => update(index, { projectId: value })}
                options={projectOptions}
                placeholder={t("expenses.noProject")}
                filter
              />
            </div>
            <div className="col-span-2 flex items-center justify-end sm:col-span-1">
              {items.length > 1 ? (
                <Button
                  type="button"
                  size="small"
                  variant="text"
                  severity="danger"
                  icon="pi pi-trash"
                  aria-label={t("expenses.removeItem")}
                  onClick={() =>
                    setItems((current) => current.filter((_, i) => i !== index))
                  }
                />
              ) : null}
            </div>
          </div>
        ))}
        <Button
          type="button"
          size="small"
          variant="outlined"
          severity="secondary"
          icon="pi pi-plus"
          onClick={() => setItems((current) => [...current, emptyItem()])}
        >
          {t("expenses.addItem")}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_1fr]">
        <Field label={t("expenses.currency")}>
          <SelectInput
            value={currency}
            onChange={setCurrency}
            options={CURRENCIES.map((code) => ({ value: code, label: code }))}
          />
        </Field>
        <Field label={t("expenses.notes")}>
          <Input
            fluid
            value={notes}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setNotes(event.target.value)
            }
          />
        </Field>
      </div>
      <p className="text-xs text-app-muted">
        {t("expenses.receiptsAfterSave")}
      </p>
    </Modal>
  );
}
